/**
 * rolePermissionsApi.js — client for org-scoped role-permissions.
 *
 * VENDOR MODE NOTICE (2026-04-21):
 * The vendor API (AriaOne / ehi.ariaone.ai) does NOT expose
 * `/api/role-permissions`. Calling it in vendor mode produces a 404 on
 * the vendor edge (or worse, on the login/SSO host). In vendor mode we
 * short-circuit EVERY helper below with `NOT_SUPPORTED_IN_VENDOR_MODE`
 * (no network request issued) so the Roles tab derives its display
 * purely from the team store + `/api/user-access/me` permissions payload.
 *
 * The legacy backend path stays intact for local / dev environments, so
 * in non-vendor mode every helper keeps its original behavior.
 */
import { get, put, patch, requireParam, VENDOR_MODE_ENABLED } from './httpClient';

const BASE = '/api/role-permissions';

/**
 * Returns `true` only when the internal (local backend) role-permissions
 * endpoints are safe to call — i.e. NOT running against the vendor edge.
 * Callers that guard on this explicitly get a chance to render a friendly
 * "defaults-only" state without waiting for a network round-trip to 404.
 */
export const isRolePermissionsSupported = () => !VENDOR_MODE_ENABLED;

const vendorSoftFail = () => Promise.resolve({
  ok: false,
  error: {
    status: 0,
    code: 'NOT_SUPPORTED_IN_VENDOR_MODE',
    message: 'Role-permissions API is not available in vendor mode; UI uses defaults + /user-access/me.',
  },
});

/** Returns { roles: [{role_id, permissions, source, updated_at}], keys: {...} } */
export const listRolePermissions = () => {
  if (!isRolePermissionsSupported()) return vendorSoftFail();
  return get(`${BASE}`);
};

/** Returns { role_id, permissions } merging DB overrides over defaults. */
export const getRolePermissions = (roleId) => {
  requireParam('roleId', roleId);
  if (!isRolePermissionsSupported()) return vendorSoftFail();
  return get(`${BASE}/${roleId}`);
};

/** Overwrite (merge) a full permission map. */
export const setRolePermissions = (roleId, permissions) => {
  requireParam('roleId', roleId);
  if (!isRolePermissionsSupported()) return vendorSoftFail();
  return put(`${BASE}/${roleId}`, { permissions: permissions || {} });
};

/** Toggle a single permission key for a role. */
export const toggleRolePermission = (roleId, key, value) => {
  requireParam('roleId', roleId);
  requireParam('key', key);
  if (!isRolePermissionsSupported()) return vendorSoftFail();
  return patch(`${BASE}/${roleId}`, { key, value: !!value });
};

/** Meta introspection (defaults per role + key buckets). */
export const getPermissionDefaults = () => {
  if (!isRolePermissionsSupported()) return vendorSoftFail();
  return get(`${BASE}/meta/defaults`);
};
